import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import TariffSearch from "./searchTariffs";
import NappiSearch from "./searchNappi";
import AddModifierModal from "../Accounts/Modifier";

export function AddServicesPricing({
  onPricingChange = () => {},
  onValidate = () => {},
  onErrorsChange = () => {},
  servicePricing,            // pricing object from DB when editing
  selectedPractices,         // array of practiceNumbers
  pracName,                  // array of practice objects (needs practiceDisciplineCode)
  pracNameEdit,
  user,
}) {
  // ─────────────────────────────────────────────────────────────
  // Local state
  // ─────────────────────────────────────────────────────────────
  const [pricing, setPricing] = useState({
    invoiceDescription: "",
    flatRate: "",
    paymentOptions: { useFlatRate: true, useFlatAndMedicalAidRate: false },
    selectedItems: [], // [{ id, kind: 'tariff'|'nappi', selected, code, description, rate, modifiers }]
  });
  const [errors, setErrors] = useState({});
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [touched, setTouched] = useState(false);

  // block onPricingChange while hydrating from props
  const isHydratingRef = useRef(false);

  // ─────────────────────────────────────────────────────────────
  // Helpers: stable IDs & normalization
  // ─────────────────────────────────────────────────────────────
  const makeStableId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2,9)}`;


  const normalizeForSave = (local) => ({
    invoiceDescription: local.invoiceDescription,
    flatRate: local.flatRate,
    paymentOptions: local.paymentOptions,
    selectedTariffs: local.selectedItems
      .filter((i) => i.kind === "tariff" && i.selected)
      .map((i) => ({
        code: i.code,
        description: i.description,
        rate: String(i.rate ?? "0.00"),
        modifiers: Array.isArray(i.modifiers) ? i.modifiers.map(m => ({ code: m.code })) : [],
      })),
    selectedNappi: local.selectedItems
      .filter((i) => i.kind === "nappi" && i.selected)
      .map((i) => ({
        nappiCode: i.code,
        name: i.description,
        rate: String(i.rate ?? "0.00"),
      })),
  });

  const coreFromService = (sp) => ({
    invoiceDescription: sp?.invoiceDescription || "",
    flatRate: sp?.flatRate || "",
    paymentOptions: sp?.paymentOptions || { useFlatRate: true, useFlatAndMedicalAidRate: false },
    selectedTariffs: (sp?.selectedTariffs || []).map((t) => ({
      code: t.code || "",
      description: t.description || "",
      rate: String(t.rate ?? "0.00"),
      modifiers: Array.isArray(t.modifiers) ? t.modifiers.map(m => ({ code: m.code })) : [],
    })),
    selectedNappi: (sp?.selectedNappi || sp?.selectedNappis || []).map((n) => ({
      nappiCode: n.nappiCode || "",
      name: n.name || "",
      rate: String(n.rate ?? "0.00"),
    })),
  });

  const deepEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const safePropagate = (local) => {
    if (isHydratingRef.current) return;
    onPricingChange(normalizeForSave(local));
  };

  // ─────────────────────────────────────────────────────────────
  // Practice / discipline derivation (controls tariff search availability)
  // ─────────────────────────────────────────────────────────────
  const selectedPracticeObjs = (pracName || []).filter((p) =>
    (selectedPractices || []).includes(p.practiceNumber)
  );

  const disciplineCodes = Array.from(
    new Set(
      selectedPracticeObjs.map((p) => p.practiceDisciplineCode).filter(Boolean)
    )
  );

  const hasDisciplineMismatch = disciplineCodes.length > 1;
  const practiceDisciplineCode = disciplineCodes[0] || "";

  const practiceNumber =
    selectedPracticeObjs[0]?.practiceNumber ??
    (Array.isArray(selectedPractices) ? selectedPractices[0] : undefined) ??
    pracNameEdit;

  const canAddTariff = !!practiceNumber && !hasDisciplineMismatch;

  // ─────────────────────────────────────────────────────────────
  // Reset for brand-new pricing
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!servicePricing) {
      setPricing({
        invoiceDescription: "",
        flatRate: "",
        paymentOptions: { useFlatRate: true, useFlatAndMedicalAidRate: false },
        selectedItems: [],
      });
      setErrors({});
    }
  }, [servicePricing]);

  // ─────────────────────────────────────────────────────────────
  // Hydrate from servicePricing (only if content differs)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!servicePricing) return;

    const incomingCore = coreFromService(servicePricing);
    const localCore = normalizeForSave(pricing);

    // If incoming content == what we already represent, skip rehydration.
    if (deepEqual(incomingCore, localCore)) return;

    isHydratingRef.current = true;

    const items = [];

   incomingCore.selectedTariffs.forEach((t) => {
  items.push({
    id: makeStableId("tariff", t.code || "code"),
    kind: "tariff",
    selected: t,
    code: t.code,
    description: t.description,
    rate: t.rate,
    modifiers: t.modifiers || [],
  });
});
incomingCore.selectedNappi.forEach((n) => {
  items.push({
    id: makeStableId("nappi", n.nappiCode || "code"),
    kind: "nappi",
    selected: n,
    code: n.nappiCode,
    description: n.name,
    rate: n.rate,
    modifiers: [],
  });
});


    setPricing({
      invoiceDescription: incomingCore.invoiceDescription,
      flatRate: incomingCore.flatRate,
      paymentOptions: incomingCore.paymentOptions,
      selectedItems: items,
    });

    // let React commit, then allow propagation again
    queueMicrotask(() => {
      isHydratingRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicePricing]);

  // ─────────────────────────────────────────────────────────────
  // Auto-total from line items
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const total = pricing.selectedItems.reduce((sum, i) => {
      const num = parseFloat(String(i.rate || "").replace(/[^0-9.]/g, "")) || 0;
      return sum + num;
    }, 0);
    const formatted = `R ${total.toFixed(2)}`;
    if (pricing.flatRate !== formatted) {
      const updated = { ...pricing, flatRate: formatted };
      setPricing(updated);
      safePropagate(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing.selectedItems]);

  // Always give a validator back to parent
  useEffect(() => {
    onValidate(() => validatePricing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing, hasDisciplineMismatch, practiceDisciplineCode]);

  // ─────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────
  const addItem = (kind) => {
  const newItem = {
    id: makeStableId(kind, "new"),
    kind,
    selected: null,
    code: "",
    description: "",
    rate: "0.00",
    modifiers: [],
  };
  setPricing(prev => {
    const updated = { ...prev, selectedItems: [...prev.selectedItems, newItem] };
    safePropagate(updated);
    return updated;
  });
};


 const selectItem = (id, data) => {
  setPricing(prev => {
    const updatedItems = prev.selectedItems.map((i) => {
      if (i.id !== id) return i;

      const isNappi = i.kind === "nappi";
      const code = isNappi ? (data.nappiCode || data.code || "") : (data.code || "");
      const desc = isNappi
        ? (data.name || "")
        : `${data.description ?? ""}${data.typeCode ? ` (${data.typeCode})` : ""}`;
      const rateRaw = data.rate ?? "0.00";
      const rate = typeof rateRaw === "number" ? rateRaw.toFixed(2) : String(rateRaw);

      return { ...i, selected: data, code, description: desc, rate };
    });
    const updated = { ...prev, selectedItems: updatedItems };
    safePropagate(updated);
    return updated;
  });
};


 const updateRate = (id, val) => {
  setPricing(prev => {
    const updatedItems = prev.selectedItems.map(i =>
      i.id === id ? { ...i, rate: val } : i
    );
    const updated = { ...prev, selectedItems: updatedItems };
    safePropagate(updated);
    return updated;
  });
};


  const handleApplyModifier = (modifierCode, selectedLineNumbers /* 1-based */) => {
    const tariffItemsInModalOrder = pricing.selectedItems.filter(
      (i) => i.kind === "tariff" && i.selected
    );
    const idsToApply = selectedLineNumbers
      .map((n) => tariffItemsInModalOrder[n - 1]?.id)
      .filter(Boolean);

    const updatedItems = pricing.selectedItems.map((item) => {
      if (item.kind !== "tariff") return item;
      if (!idsToApply.includes(item.id)) return item;
      const newMods = [...(item.modifiers || []), { code: modifierCode }];
      return { ...item, modifiers: newMods };
    });

    const updated = { ...pricing, selectedItems: updatedItems };
    setPricing(updated);
    safePropagate(updated);
  };

  const validatePricing = () => {
    const newErrors = {};
    if (!pricing.invoiceDescription) {
      newErrors.invoiceDescription = "Invoice description is required.";
    }
    if (hasDisciplineMismatch) {
      newErrors.discipline =
        "Selected practices have different discipline codes. Please select practices with the same discipline to add tariffs.";
    }
    if (
      pricing.paymentOptions.useFlatRate &&
      pricing.selectedItems.filter((i) => i.kind === "tariff").length === 0 &&
      !pricing.flatRate
    ) {
      newErrors.flatRate = "Enter a total flat rate or add at least one tariff.";
    }
    setErrors(newErrors);
    onErrorsChange(newErrors);
    return !Object.keys(newErrors).length;
  };

  const codeWithMods = (item) => {
    if (item.kind !== "tariff") return item.code;
    const mods = (item.modifiers || []).map((m) => m.code).filter(Boolean);
    return mods.length ? `${item.code} (${mods.join(", ")})` : item.code;
  };

  const tariffLines = pricing.selectedItems.filter((i) => i.kind === "tariff" && i.selected);
  const modifierButtonDisabled = tariffLines.length === 0;

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <Form>
        {/* Payment Options */}
        <Row className="mb-2">
          <Col>
            <Form.Check
              type="radio"
              label="Flat Rate for All Patients"
              name="rateOpt"
              checked={pricing.paymentOptions.useFlatRate}
              onChange={() => {
                const updated = {
                  ...pricing,
                  paymentOptions: { useFlatRate: true, useFlatAndMedicalAidRate: false },
                };
                setPricing(updated);
                safePropagate(updated);
              }}
            />
            <Form.Check
              type="radio"
              label="Flat Rate + Medical Aid Rates"
              name="rateOpt"
              checked={pricing.paymentOptions.useFlatAndMedicalAidRate}
              onChange={() => {
                const updated = {
                  ...pricing,
                  paymentOptions: { useFlatRate: false, useFlatAndMedicalAidRate: true },
                };
                setPricing(updated);
                safePropagate(updated);
              }}
            />
          </Col>
        </Row>

        {/* Description */}
        <Row className="mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Invoice Description</Form.Label>
              <Form.Control
                type="text"
                name="invoiceDescription"
                value={pricing.invoiceDescription}
                isInvalid={!!errors.invoiceDescription}
                onChange={(e) => {
                  const updated = { ...pricing, invoiceDescription: e.target.value };
                  setTouched(true);
                  setPricing(updated);
                  safePropagate(updated);
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.invoiceDescription}
              </Form.Control.Feedback>
              {errors.discipline && (
                <div className="text-danger small mt-2">{errors.discipline}</div>
              )}
            </Form.Group>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row className="mb-2">
          <Col>
            <Button
              style={{ width: "120px" }}
              variant="outline-primary"
              onClick={() => addItem("tariff")}
              disabled={!canAddTariff}
              title={
                !practiceNumber
                  ? "Select a practice first"
                  : hasDisciplineMismatch
                  ? "Selected practices have different disciplines"
                  : ""
              }
            >
              + Tariff
            </Button>{" "}
            <Button
              style={{ width: "120px" }}
              variant="outline-primary"
              onClick={() => addItem("nappi")}
            >
              + Nappi
            </Button>{" "}
            <Button
              variant="outline-primary"
              style={{ width: "120px" }}
              onClick={() => setShowModifierModal(true)}
              disabled={modifierButtonDisabled}
              title={modifierButtonDisabled ? "Add a tariff line first" : ""}
            >
              + Modifier
            </Button>
          </Col>
        </Row>

        {/* Items List */}
        {pricing.selectedItems.map((item) => (
          <Row key={item.id} className="align-items-center mb-2">
            <Col md={3}>
              {!item.selected && item.kind === "tariff" ? (
                <TariffSearch
                  practiceNumber={practiceNumber}
                  onTariffCodeSelect={(t) => selectItem(item.id, t)}
                />
              ) : !item.selected && item.kind === "nappi" ? (
                <NappiSearch onNappiSelect={(n) => selectItem(item.id, n)} />
              ) : (
                <Form.Control readOnly value={codeWithMods(item)} />
              )}
            </Col>

            <Col md={6}>
              {item.selected && <Form.Control readOnly value={item.description} />}
            </Col>

            {(item.kind === "tariff" || item.kind === "nappi") && item.selected && (
              <>
                <Col md={2}>
                  <Form.Control
                    className="text-end"
                    value={item.rate}
                    onChange={(e) => updateRate(item.id, e.target.value)}
                    onBlur={(e) => {
                      const num =
                        parseFloat(e.target.value.replace(/[^\d.]/g, "")) || 0;
                      updateRate(item.id, num.toFixed(2));
                    }}
                  />
                </Col>

                <Col md={1} className="text-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                   onClick={() => {
  setPricing(prev => {
    const updated = {
      ...prev,
      selectedItems: prev.selectedItems.filter(i => i.id !== item.id),
    };
    safePropagate(updated);
    return updated;
  });
}}

                  >
                    <i className="fa fa-trash" />
                  </Button>
                </Col>
              </>
            )}
          </Row>
        ))}

        {/* Flat Rate Total */}
        <Row className="mt-4">
          <Col md={{ span: 3, offset: 6 }} className="text-end fw-bold">
            Total Flat Rate:
          </Col>
          <Col md={2}>
            <Form.Control
              type="text"
              className="text-end"
              value={pricing.flatRate}
              isInvalid={!!errors.flatRate}
              onChange={(e) => {
                setTouched(true);
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const formatted = `R ${parseFloat(raw || "0").toFixed(2)}`;
                const updated = { ...pricing, flatRate: formatted };
                setPricing(updated);
                safePropagate(updated);
              }}
            />
            {errors.flatRate && (
              <div className="text-danger small">{errors.flatRate}</div>
            )}
          </Col>
        </Row>
      </Form>

      {/* Modifiers modal — tariff lines only */}
      <AddModifierModal
        show={showModifierModal}
        onHide={() => setShowModifierModal(false)}
        practiceDisciplineCode={practiceDisciplineCode}
        invoiceLines={pricing.selectedItems
          .filter((i) => i.kind === "tariff" && i.selected)
          .map((i, idx) => ({
            lineNumber: idx + 1, // 1-based within tariffs only
            tariffCode: i.code,
            description: i.description,
            quantity: 1,
            rate: parseFloat(i.rate) || 0,
            modifier: i.modifiers,
          }))}
        onSave={handleApplyModifier}
        setLines={() => {}}
      />
    </>
  );
}
